import { LoadingSpinnerIcon } from '#app/__generated__/components/Icons/LoadingSpinner.js';
import { type Theme } from '#app/theme/index.js';
import { type ButtonSize, type ButtonVariant } from './types.js';
import { css, keyframes, styled } from 'styled-components';

type ButtonLoadingSpinnerProps = {
  readonly size: ButtonSize;
  readonly variant: ButtonVariant;
};

const getSpinnerColor = ({
  theme,
  variant,
}: {
  theme: Theme;
  variant: ButtonVariant;
}): string => {
  switch (variant) {
    case 'V2Destructive':
    case 'V2Primary':
      return theme.colorsV2.white;
    case 'V2Link':
    case 'V2Secondary':
    case 'V2TertiaryOutline':
    case 'V2TertiarySolid':
      return theme.colorsV2.gray80;

    default:
      return theme.colorsV2.white;
  }
};

const getSpinnerSize = (size: ButtonSize): number => {
  switch (size) {
    case 'small': {
      return 20;
    }

    case 'xsmall': {
      return 16;
    }

    case 'large':
    case 'medium':
    default: {
      return 28;
    }
  }
};

const spin = keyframes`
  0% {
    transform: rotateZ(0deg);
  }

  100% {
    transform: rotateZ(360deg);
  }
`;

const LoadingSpinner = styled.div<{
  size: ButtonSize;
  variant: ButtonVariant;
}>`
  ${({ size, theme, variant }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    height: ${getSpinnerSize(size)}px;
    width: ${getSpinnerSize(size)}px;
    animation: ${spin} 1s linear 0s infinite;
    height: 100%;
    user-select: none;
    color: ${getSpinnerColor({ theme, variant })};
    transform-origin: center;

    > svg {
      transform-origin: center;
      height: 16px;
      width: 16px;
      transform-origin: center;
    }
  `}
`;

export const ButtonLoadingSpinner = ({
  size,
  variant,
}: ButtonLoadingSpinnerProps) => {
  return (
    <LoadingSpinner
      aria-label="loading spinner"
      role="img"
      size={size}
      variant={variant}
    >
      <LoadingSpinnerIcon />
    </LoadingSpinner>
  );
};
